import type { EventEmitter, VNode } from '@stencil/core';
import { Component, Element, Event, getAssetPath, h, Host, Listen, State } from '@stencil/core';
import type { CustomAPI, UserAPI } from '@sydle/ui-api';
import { getAPI } from '@sydle/ui-api';
import type { ComponentContentType } from '@sydle/ui-utils';
import {
  Consumer,
  getApp,
  getConfig,
  getRouteByIdentifier,
  navigate,
  renderComponent,
  setHTMLTitle,
} from '@sydle/ui-utils';
import type { RouterLocation } from '@vaadin/router';
import type { UIAppConfig } from '../../types/ui-app-config';

@Component({
  tag: 'sy-app-custom-page',
  styleUrl: 'scss/index.scss',
  assetsDirs: ['assets'],
  shadow: false,
})
export class CustomPage {
  @Element() el: HTMLSyAppCustomPageElement;

  @State() elements: VNode[] = [];

  private location: RouterLocation;
  private consumer: Consumer;

  /**
   * Emitted when page content is loaded
   */
  @Event() syAppCustomPageDidLoadElements: EventEmitter;

  async componentWillLoad() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe('ui-app.location', async (newLocation: RouterLocation) => {
      if (!newLocation) {
        return;
      }

      this.location = newLocation;
      this.elements = await this.getPageElements();
      this.setTitle();
    });
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }

  @Listen('syAppCustomPageReloadElements', { target: 'body' })
  async onSyAppCustomPageReloadElements(): Promise<void> {
    this.elements = await this.getPageElements();
    this.setTitle();
  }

  @Listen('syAppRecoverPasswordBackToLogin')
  async onSyAppRecoverPasswordBackToLogin(): Promise<void> {
    const uiAppConfig = getConfig<UIAppConfig>('ui-app');
    const authRouteIdentifier = uiAppConfig?.authRoute;
    if (authRouteIdentifier) {
      navigate(authRouteIdentifier);
    }
  }

  private async getPageElements(): Promise<VNode[]> {
    const contents = await this.getPageContents();
    this.syAppCustomPageDidLoadElements.emit();
    window.scrollTo(0, 0);
    return contents.map((content) => renderComponent(content));
  }

  private getSearchParamsObject() {
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.size === 0) {
      return null;
    }

    const paramsObject = {};

    for (const [key, value] of searchParams.entries()) {
      paramsObject[key] = value;
    }

    return paramsObject;
  }

  private async getPageContents(): Promise<ComponentContentType[]> {
    const syAppAPI = getAPI<CustomAPI>('syAppAPI');
    if (!this.location || !syAppAPI) return [];

    const routeName = this.location.route.name;

    if (routeName === '404') {
      const response = await fetch(getAssetPath('./assets/404.html'));
      const fallbackHTML = await response.text();
      return [{ tag: 'div', node: { innerHTML: fallbackHTML }, children: [] }];
    }

    if (!routeName) return [];

    const searchParamsObject = this.getSearchParamsObject();

    const pageContent = await syAppAPI.fetch<
      {
        appId: string;
        routeName: string;
        lang: string;
        hash: string;
        headers: null;
        pathname: string;
        searchParams?: Record<string, string>;
        fragment?: string;
      },
      { content: ComponentContentType[] }
    >('getPageContent', {
      appId: getApp()?.identifier ?? '',
      routeName,
      headers: null,
      ...(searchParamsObject ? { searchParams: searchParamsObject } : {}),
      lang: getAPI<UserAPI>('userAPI')?.getCurrentLanguage(),
      hash: getApp()?.hash,
      pathname: window.location.pathname,
      ...(window.location.hash ? { fragment: window.location.hash } : {}),
    });
    const content = pageContent.data?.content;

    return content ?? [];
  }

  private setTitle() {
    const routeName = this.location?.route?.name;
    if (!routeName) return;

    const route = getRouteByIdentifier(routeName);
    if (route?.dynamicTitle) return;

    const userAPI = getAPI<UserAPI>('userAPI');
    const currentLanguage = userAPI?.getCurrentLanguage() ?? '';

    const title = route?.title__i18n?.[currentLanguage] ?? route?.title;

    const uiAppConfig = getConfig<UIAppConfig>('ui-app');
    const sufix = uiAppConfig?.titleSufix__i18n?.[currentLanguage] ?? uiAppConfig?.titleSufix;

    setHTMLTitle(title, sufix);
  }

  render() {
    if (!this.elements.length) {
      return <Host></Host>;
    }

    return <Host>{this.elements}</Host>;
  }
}
